import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwg5m1Api, ReverseCandidate } from '../../services/pwg5m1Api';
import { auditLogApi } from '../../services/auditLogApi';
import { useModulePermission } from '../../hooks/usePermission';
import {
  AcceptanceDataPanel,
  AcceptanceFieldHint,
  AcceptanceToolbar,
  pwg5m1f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import './PWG5M1Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const PWG5M1Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwg5m1f1');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [acceptanceLastApiResponse, setAcceptanceLastApiResponse] = useState<unknown>(null);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<ReverseCandidate | null>(null);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const [acceptanceFocusNo, setAcceptanceFocusNo] = useState('');
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwg5m1f1AcceptanceManifest.formId);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;
  const selectedNos = items.filter((s) => s.selected).map((s) => s.item.fg110_01);
  const documentKey = acceptanceFocusNo || selectedNos[0] || items[0]?.item.fg110_01;

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [database, audit] = await Promise.allSettled([
      documentKey
        ? pwg5m1Api.queryCandidates(documentKey, documentKey)
        : Promise.resolve({ items: [], total: 0 }),
      auditLogApi.list({
        form_id: pwg5m1f1AcceptanceManifest.formId,
        doc_key: documentKey,
        page: 1,
        pageSize: 20,
      }),
    ]);
    setAcceptanceDatabaseRecord(
      database.status === 'fulfilled' ? database.value.items?.[0] || null : null
    );
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(database.status === 'rejected' ? ['可沖銷資料重新讀取失敗。'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗；目前不能確認沖銷留痕。'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, documentKey]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    const sample = items[0]?.item;
    if (!sample) {
      message.warning('請先查詢可沖銷資料；自動填表不會直接執行沖銷。');
      return;
    }
    setStartNo(sample.fg110_01);
    setEndNo(sample.fg110_01);
    setAcceptanceFocusNo(sample.fg110_01);
    setItems((current) => current.map((row, index) => ({ ...row, selected: index === 0 })));
    message.success('已帶入第一筆候選並只勾選一張；仍需由你按存檔並確認沖銷。');
  }, [items]);

  const H = (fieldId: string) => (
    <AcceptanceFieldHint
      manifest={pwg5m1f1AcceptanceManifest}
      fieldId={fieldId}
      documentKey={documentKey}
    />
  );

  // ---- Query ----
  const onQuery = useCallback(async () => {
    const start = startNo.trim();
    const end = endNo.trim();
    if (start && end && start > end) {
      message.warning('起始繳款單號不可大於結束繳款單號');
      return;
    }
    if (!permissions.canView) {
      message.error('沒有 G5M1 查詢權限');
      return;
    }
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwg5m1Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setAcceptanceLastApiResponse(res);
      setAcceptanceFocusNo(mapped[0]?.item.fg110_01 || '');
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的繳款單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo, permissions.canView]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    if (checked) setAcceptanceFocusNo(no);
    setItems((prev) =>
      prev.map((s) =>
        s.item.fg110_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (!permissions.canApprove) {
      message.error('沒有 G5M1 沖銷權限');
      return;
    }
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的繳款單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆繳款單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fg110_01);
        setExecuting(true);
        try {
          const res = await pwg5m1Api.reverse(selectedNos);
          setAcceptanceLastApiResponse(res);
          setAcceptanceFocusNo(selectedNos[0] || '');
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items, permissions.canApprove]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fg110_01, e.target.checked)}
        />
      ),
    },
    {
      title: '繳款單號',
      dataIndex: ['item', 'fg110_01'],
      key: 'fg110_01',
      width: 120,
    },
    {
      title: '繳款日期',
      dataIndex: ['item', 'fg110_11'],
      key: 'fg110_11',
      width: 110,
    },
    {
      title: '廠商代號',
      dataIndex: ['item', 'fg110_26'],
      key: 'fg110_26',
      width: 110,
    },
    {
      title: '部門',
      dataIndex: ['item', 'fg110_19'],
      key: 'fg110_19',
      width: 100,
    },
    {
      title: '繳款類型',
      dataIndex: ['item', 'fg110_17'],
      key: 'fg110_17',
      width: 100,
    },
    {
      title: '核准狀態',
      dataIndex: ['item', 'fg110_18'],
      key: 'fg110_18',
      width: 90,
    },
    {
      title: '水單匯率',
      dataIndex: ['item', 'fg110_24'],
      key: 'fg110_24',
      width: 90,
      align: 'right',
    },
    {
      title: '台幣繳款金額',
      dataIndex: ['item', 'fg110_12'],
      key: 'fg110_12',
      width: 120,
      align: 'right',
    },
    {
      title: '外幣繳款金額',
      dataIndex: ['item', 'fg110_13'],
      key: 'fg110_13',
      width: 120,
      align: 'right',
    },
  ];

  // ---- Render ----
  return (
    <div className="pwg5m1-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <AcceptanceToolbar
          manifest={pwg5m1f1AcceptanceManifest}
          onAutoFill={handleAcceptanceAutoFill}
          documentKey={documentKey}
          autoFillDisabled={items.length === 0 || executing}
          autoFillReason={items.length === 0
            ? '請先查詢可沖銷資料。'
            : '只會帶入查詢範圍並勾選第一筆，不會自動執行沖銷。'}
        />

        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                應付帳款管理
              </a>
              <span>/</span>
              <span className="current">繳款單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>繳款單核准沖銷</h1>
            <span className="badge">G5M1</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fg110_01">繳款單號 .從:</label>
          <Input
            aria-label="起始繳款單號"
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            aria-label="結束繳款單號"
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button
            type="primary"
            data-acceptance-action="PWG5M1-ACT-QUERY-CANDIDATES"
            onClick={onQuery}
            loading={loading}
            disabled={!permissions.canView}
          >
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {acceptanceEnabled && (
            <div style={{ marginBottom: 12, padding: 10, background: '#fff7e6', borderRadius: 6 }}>
              <strong>候選欄位說明：</strong>{' '}
              勾選沖銷項目 {H('selected_items')}　繳款單號 {H('fg110_01')}　繳款日期 {H('fg110_11')}　
              台幣金額 {H('fg110_12')}　外幣金額 {H('fg110_13')}　繳款類別 {H('fg110_17')}　
              過帳狀態 {H('fg110_18')}　部門 {H('fg110_19')}　水單匯率 {H('fg110_24')}　
              廠商代號 {H('fg110_26')}　核准狀態 {H('fg110_80')}　沖銷版本號 {H('fg110_90')}
            </div>
          )}
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fg110_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入繳款單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            data-acceptance-action="PWG5M1-ACT-REVERSE"
            size="large"
            disabled={!permissions.canApprove || selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>

        <AcceptanceDataPanel
          manifest={pwg5m1f1AcceptanceManifest}
          documentKey={documentKey}
          snapshots={{
            screenDraft: {
              query: { start_no: startNo, end_no: endNo },
              selected_items: selectedNos,
              candidates: items.map((row) => row.item),
            },
            lastApiResponse: acceptanceLastApiResponse,
            databaseRecord: acceptanceDatabaseRecord,
          }}
          latestRecords={items.slice(0, 3).map((row) => row.item)}
          history={[]}
          auditEntries={acceptanceAudit}
          loading={acceptanceLoading || loading || executing}
          onRefresh={() => { void loadAcceptanceData(); }}
          observationErrors={acceptanceErrors}
        />
      </div>
    </div>
  );
};

export default PWG5M1Form;
